
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import DayCircles from '@/components/DayCircles';

interface DemoSectionProps {
  onConfettiTrigger: () => void;
}

const DemoSection = ({ onConfettiTrigger }: DemoSectionProps) => {
  const [demoPact, setDemoPact] = useState('');
  const [demoCompleted, setDemoCompleted] = useState(false);
  const [completedDays, setCompletedDays] = useState(0);
  const [showClickIndicator, setShowClickIndicator] = useState(false);

  const createDemoPact = () => {
    if (demoPact.trim()) {
      setShowClickIndicator(true);
    }
  };

  const markDemoComplete = () => {
    setDemoCompleted(true);
    setCompletedDays(prev => prev + 1);
    setShowClickIndicator(false);
    onConfettiTrigger();
    
    // Reset for next interaction after a delay
    setTimeout(() => {
      setDemoCompleted(false);
      if (completedDays < 4) { // Show indicator for next day if not at end (5 days = 0-4)
        setShowClickIndicator(true);
      }
    }, 2000);
  };

  const resetDemo = () => {
    setDemoCompleted(false);
    setCompletedDays(0);
    setShowClickIndicator(demoPact.trim() ? true : false);
  };

  return (
    <section className="container max-w-4xl mx-auto px-4 py-20">
      <h3 className="text-3xl font-bold text-center text-deep-blue mb-4">
        Try Creating Your First PACT
      </h3>
      <p className="text-center text-deep-blue/70 mb-12 max-w-2xl mx-auto text-lg">
        Experience how simple it is. No commitment needed - just curiosity.
      </p>
      
      <div className="bg-white rounded-3xl p-8 shadow-xl border border-soft-blue/50 max-w-2xl mx-auto backdrop-blur-sm">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-deep-blue mb-2">
              What would you like to show up for?
            </label>
            <div className="flex gap-3">
              <Input
                value={demoPact}
                onChange={(e) => setDemoPact(e.target.value)}
                placeholder="e.g., Read for 10 minutes"
                className="flex-1"
              />
              <Button 
                onClick={createDemoPact}
                disabled={!demoPact.trim()}
                className="bg-vibrant-orange hover:bg-vibrant-orange/90"
              >
                Create
              </Button>
            </div>
          </div>

          {demoPact && (
            <div className="border border-soft-blue rounded-xl p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-deep-blue">{demoPact}</h4>
                  <p className="text-sm text-deep-blue/70">Day {completedDays + 1}</p>
                </div>
                
                <div className="relative">
                  <button
                    onClick={markDemoComplete}
                    className="w-12 h-12 bg-green-100 text-green-600 hover:bg-green-200 hover:scale-105 rounded-full text-lg transition-all relative"
                    title="Mark complete"
                  >
                    ✓
                  </button>
                  
                  {/* Click indicator on the complete button */}
                  {showClickIndicator && completedDays < 5 && (
                    <div className="absolute -bottom-16 -right-4 z-10">
                      <div className="flex flex-col items-center">
                        <div className="font-handwritten text-sm font-bold text-slate-dark mb-1 transform -rotate-2">
                          Click here
                        </div>
                        <div className="text-lg animate-bounce">👆</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Day circles visualization */}
              <DayCircles 
                completedDays={completedDays}
                totalDays={5}
                showClickIndicator={false}
              />

              {demoCompleted && (
                <div className="text-center py-4 animate-bounce-in">
                  <div className="text-3xl mb-2">🎉</div>
                  <p className="text-vibrant-orange font-semibold">Great job showing up!</p>
                  <p className="text-sm text-deep-blue/70">This is how it feels to complete a PACT</p>
                </div>
              )}

              {completedDays >= 5 && (
                <div className="text-center py-4 animate-bounce-in">
                  <div className="text-4xl mb-2">🏆</div>
                  <p className="text-vibrant-orange font-bold text-lg">Challenge completed!</p>
                  <p className="text-sm text-deep-blue/70 mb-4">Amazing consistency!</p>
                  <Button 
                    onClick={resetDemo}
                    className="bg-deep-blue hover:bg-deep-blue/90 text-white"
                  >
                    Try Another PACT
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DemoSection;
